/**
 * Compass Explorer — service-level → resource-level cost attribution.
 *
 * Cost Explorer surfaces spend at the *service* level (e.g. "Amazon
 * Relational Database Service: $556.40/mo"), but the canvas wants to
 * paint a heatmap on individual resources. Without an attribution
 * pass every resource shows `$0` even when its account spent
 * thousands.
 *
 * Approach: pure heuristic. For each cost-finding node in the graph,
 * we parse the `(accountId, serviceName, amount)` triple, map the
 * service to a set of resource types, then distribute the amount
 * evenly across resources of those types in that account. The result
 * is a `Map<resourceId, monthlySpendUsd>` that callers spread into
 * each leaf's attrs as `monthlySpend`.
 *
 * Caveats — documented so the heatmap reads correctly:
 *  - Even distribution is a *first-order* estimate. A 1024GB RDS and
 *    a 20GB RDS in the same account get the same number. Good for
 *    relative comparison across accounts, not for billing.
 *  - Services with no matching resources in the account are dropped
 *    (MongoDB Marketplace, Tax, Savings Plans credits).
 *  - "EC2 - Other" lumps EBS / NAT / EIP / data transfer. We split it
 *    across ec2 + nat + igw + eip resources because those are where
 *    the spend usually lives.
 */

import type { PersistedGraph, PersistedNode } from '../../models/KnowledgeGraph';

const GENERIC_MANIFEST_IDS = new Set(['aws_generic', 'generic', 'resource']);

function resourceTypeOf(node: PersistedNode): string {
  const props = (node.props ?? {}) as Record<string, unknown>;
  const mid = (props._manifestId as string | undefined) ?? (props.manifestId as string | undefined);
  const propRt = typeof props.resourceType === 'string' ? props.resourceType : undefined;
  const raw = propRt && (!mid || GENERIC_MANIFEST_IDS.has(mid)) ? propRt : mid ?? node.type;
  return raw.startsWith('aws_') ? raw.slice(4).toLowerCase().replace(/-/g, '_') : raw.toLowerCase().replace(/-/g, '_');
}

function accountIdOf(node: PersistedNode): string | undefined {
  const props = (node.props ?? {}) as Record<string, unknown>;
  const id = props.accountId ?? props.account_id;
  return typeof id === 'string' && id.trim() ? id.trim() : undefined;
}

/**
 * Map a Cost Explorer service name to the canonical resource types
 * (after normalisation by `resourceTypeOf`) that should absorb its
 * spend. Order matters only for readability; the matcher returns
 * the first key that matches by case-insensitive substring.
 */
const SERVICE_TO_RESOURCE_TYPES: ReadonlyArray<{ match: RegExp; types: readonly string[] }> = [
  { match: /relational database|^rds\b/i, types: ['rds', 'rds_instance', 'rds_cluster'] },
  { match: /dynamodb/i, types: ['dynamodb'] },
  { match: /elasticache/i, types: ['elasticache'] },
  { match: /redshift/i, types: ['redshift'] },
  { match: /simple storage|^s3\b/i, types: ['s3', 's3_bucket'] },
  // "EC2 - Other" covers EBS / NAT / EIP / data transfer. Spread it
  // across the network-adjacent resource types that typically dominate.
  { match: /ec2 - other/i, types: ['ec2', 'nat', 'nat_gateway', 'igw', 'internet_gateway', 'eip'] },
  { match: /elastic compute|^ec2\b/i, types: ['ec2', 'asg'] },
  { match: /lambda/i, types: ['lambda'] },
  { match: /load balanc|elasticloadbalancing/i, types: ['alb', 'nlb', 'elb'] },
  { match: /api gateway/i, types: ['api_gateway'] },
  { match: /cloudfront/i, types: ['cloudfront'] },
  { match: /eks|kubernetes/i, types: ['eks'] },
  { match: /ecs|fargate/i, types: ['ecs', 'ecs_service'] },
  { match: /cloudwatch/i, types: ['log_group', 'cloudwatch_alarm', 'cloudwatch'] },
  { match: /virtual private cloud|^vpc\b/i, types: ['nat', 'nat_gateway', 'igw', 'internet_gateway', 'vpc_endpoint'] },
  { match: /route 53|^r53\b/i, types: ['route53_zone', 'route53'] },
  { match: /secrets manager/i, types: ['secret', 'secretsmanager'] },
  { match: /kms/i, types: ['kms_key', 'kms'] },
];

function resourceTypesForService(serviceName: string): readonly string[] {
  for (const entry of SERVICE_TO_RESOURCE_TYPES) {
    if (entry.match.test(serviceName)) return entry.types;
  }
  return [];
}

export interface CostAttribution {
  /** Per-resource estimated monthly USD spend. */
  monthlySpendById: Map<string, number>;
  /** Service totals we couldn't attribute (no matching resources in account). */
  unattributedByService: Map<string, number>;
  /** Grand total of attributed spend (sum of map values). */
  attributedTotalUsd: number;
}

/**
 * Build a resource-level cost attribution from a folder's knowledge
 * graph. Pure function — safe to call on every scene build; cost is
 * O(nodes) and the constant factor is small (one pass to collect
 * findings, one pass to index resources, one pass per service).
 */
export function attributeMonthlyCosts(graph: PersistedGraph): CostAttribution {
  // 1. Index resources by (accountId, normalised resourceType) so we
  //    can count denominators quickly.
  const resourcesByAccountAndType = new Map<string, PersistedNode[]>();
  for (const node of graph.nodes) {
    const props = (node.props ?? {}) as Record<string, unknown>;
    if (props.kind === 'cost_anomaly' || props.kind === 'finding') continue;
    const account = accountIdOf(node);
    if (!account) continue;
    const rt = resourceTypeOf(node);
    if (!rt) continue;
    const key = `${account}\u0000${rt}`;
    const list = resourcesByAccountAndType.get(key) ?? [];
    list.push(node);
    resourcesByAccountAndType.set(key, list);
  }

  const monthlySpendById = new Map<string, number>();
  const unattributedByService = new Map<string, number>();
  let attributedTotalUsd = 0;

  // 2. Walk cost findings and distribute.
  for (const node of graph.nodes) {
    const props = (node.props ?? {}) as Record<string, unknown>;
    if (props.kind !== 'cost_anomaly') continue;
    const raw = (props.raw ?? {}) as Record<string, unknown>;
    const amount =
      typeof raw.amount === 'number'
        ? raw.amount
        : typeof raw.amount === 'string'
          ? Number(raw.amount)
          : NaN;
    if (!Number.isFinite(amount) || amount <= 0) continue;
    const unit = typeof raw.unit === 'string' ? raw.unit.toUpperCase() : 'USD';
    if (unit !== 'USD') continue;
    const accountId =
      typeof raw.accountId === 'string'
        ? raw.accountId
        : typeof props.accountId === 'string'
          ? (props.accountId as string)
          : undefined;
    const serviceName =
      typeof raw.serviceName === 'string'
        ? raw.serviceName
        : typeof props.serviceName === 'string'
          ? (props.serviceName as string)
          : '';
    if (!accountId || !serviceName) {
      unattributedByService.set(serviceName || 'unknown', (unattributedByService.get(serviceName || 'unknown') ?? 0) + amount);
      continue;
    }
    const targetTypes = resourceTypesForService(serviceName);
    if (targetTypes.length === 0) {
      unattributedByService.set(serviceName, (unattributedByService.get(serviceName) ?? 0) + amount);
      continue;
    }

    // Collect candidate resources across every matching type for this
    // account. Drop duplicates (a resource can't have two types).
    const candidates: PersistedNode[] = [];
    const seen = new Set<string>();
    for (const rt of targetTypes) {
      const key = `${accountId}\u0000${rt}`;
      const list = resourcesByAccountAndType.get(key);
      if (!list) continue;
      for (const r of list) {
        if (seen.has(r.id)) continue;
        seen.add(r.id);
        candidates.push(r);
      }
    }
    if (candidates.length === 0) {
      unattributedByService.set(serviceName, (unattributedByService.get(serviceName) ?? 0) + amount);
      continue;
    }

    const perResource = amount / candidates.length;
    for (const r of candidates) {
      monthlySpendById.set(r.id, (monthlySpendById.get(r.id) ?? 0) + perResource);
    }
    attributedTotalUsd += amount;
  }

  return { monthlySpendById, unattributedByService, attributedTotalUsd };
}
